import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import CommonLoader from '../CommonLoader';
import { useTranslation } from 'react-i18next';
import MasterLabel from '../common/MasterLabel';
import { currencySymbol as signOf } from '../../utils/currency';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_INDEX = {
  January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
  July: 6, August: 7, September: 8, October: 9, November: 10, December: 11,
};

// Admin revenue card. The per-country cards select which country's monthly
// earnings the graph shows (first country active by default). Current year only
// — no year selector. Earnings + per-country monthly data arrive via props from
// the real-time admin dashboard socket.
const RevenueEarnings = ({ earningsByCountry, loading }) => {
  const { t } = useTranslation('dashboard');
  const currentYear = new Date().getFullYear();

  const countries = useMemo(
    () => (Array.isArray(earningsByCountry?.data) ? earningsByCountry.data : []),
    [earningsByCountry]
  );

  const [activeCountryId, setActiveCountryId] = useState(null);

  // Default to the first country once data arrives, and keep the selection valid
  // if the country list changes.
  useEffect(() => {
    if (!countries.length) {
      setActiveCountryId(null);
      return;
    }
    setActiveCountryId((prev) =>
      prev && countries.some((c) => c.countryId === prev) ? prev : countries[0].countryId
    );
  }, [countries]);

  const active = countries.find((c) => c.countryId === activeCountryId) || countries[0] || null;
  // The selected country's sign, from its currency code (₹ for INR) — never a
  // euro sign guessed for a country that did not name one.
  const currencySymbol = signOf(active);

  // Selected country's monthly series, Jan → current month.
  const monthly = useMemo(() => {
    const src = Array.isArray(active?.monthly) ? active.monthly : [];
    const byMonth = {};
    src.forEach((m) => {
      const idx = typeof m.month === 'number' ? m.month - 1 : MONTH_INDEX[m.monthName];
      if (idx != null && idx >= 0) byMonth[idx] = Number(m.totalEarnings) || 0;
    });
    const endMonth = new Date().getMonth(); // 0-11, current year
    const out = [];
    for (let i = 0; i <= endMonth; i++) {
      out.push({ month: t(`dashboard.months_short.${MONTH_KEYS[i]}`), amount: byMonth[i] || 0 });
    }
    return out;
  }, [active, t]);

  const total = active?.totalEarnings || 0;
  const avg = monthly.length ? Math.round(total / monthly.length) : 0;

  const customTooltip = ({ active: isActive, payload }) => {
    if (isActive && payload && payload[0]) {
      const { month, amount } = payload[0].payload;
      return (
        <div data-testid="revenue-earnings-tooltip" className="bg-white p-2 rounded shadow-lg border">
          <p data-testid="revenue-earnings-tooltip-text" className="text-sm font-medium">
            {month}: {currencySymbol} {amount.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  const formatYAxis = (value) => `${currencySymbol} ${(value / 1000).toFixed(0)}k`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className="bg-white w-full rounded-xl p-4 border border-gray-200/50 shadow-md"
      data-testid="revenue-earnings-container"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center" data-testid="revenue-earnings-title">
          <span className="text-lg sm:text-xl mr-2 text-[#4ABDBD] font-bold" data-testid="revenue-earnings-currency-symbol">
            {currencySymbol}
          </span>
          {t('dashboard.revenue_earnings')}
        </h3>
        <span className="text-sm font-semibold text-gray-500" data-testid="revenue-earnings-year">{currentYear}</span>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12" data-testid="revenue-earnings-loading">
          <CommonLoader />
          <span className="text-sm text-gray-600 mt-2" data-testid="revenue-earnings-loading-text">
            {t('dashboard.loading_earnings_data')}
          </span>
        </div>
      ) : countries.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-12" data-testid="revenue-earnings-no-country-data">
          {t('dashboard.no_country_earnings', 'No earnings data available')}
        </p>
      ) : (
        <>
          {/* Country selector cards — click to drive the graph below */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4" data-testid="revenue-earnings-country-grid">
            {countries.map((country) => {
              const isActive = country.countryId === active?.countryId;
              return (
                <button
                  key={country.countryId}
                  type="button"
                  onClick={() => setActiveCountryId(country.countryId)}
                  data-testid={`revenue-earnings-country-card-${country.countryId}`}
                  className={`text-left rounded-xl p-4 border shadow-sm transition-all duration-200 ${
                    isActive
                      ? 'border-[#4ABDBD] ring-2 ring-[#4ABDBD]/30 bg-[#4ABDBD]/5'
                      : 'border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-gray-800" data-testid={`revenue-earnings-country-name-${country.countryId}`}>
                      <MasterLabel type="masterCountry" id={country.countryId} field="countryName" fallback={country.countryName} />
                    </h4>
                    <span className="text-xs text-gray-500" data-testid={`revenue-earnings-country-doctor-count-${country.countryId}`}>
                      {country.doctorCount} {t('dashboard.doctors', 'doctors')}
                    </span>
                  </div>
                  <p className="text-xl font-bold text-[#4ABDBD]" data-testid={`revenue-earnings-country-total-${country.countryId}`}>
                    {signOf(country)} {country.totalEarnings.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-testid={`revenue-earnings-country-appointments-${country.countryId}`}>
                    {country.appointmentCount} {t('dashboard.completed_appointments', 'completed appointments')}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Selected-country summary */}
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3A9B9B]/5 rounded-lg mb-4" data-testid="revenue-earnings-summary-section">
            <div>
              <p className="text-sm font-medium text-gray-600" data-testid="revenue-earnings-total-label">
                {active ? (
                  <MasterLabel type="masterCountry" id={active.countryId} field="countryName" fallback={active.countryName} />
                ) : ''}{' '}· {currentYear}
              </p>
              <p className="text-2xl font-bold text-gray-900" data-testid="revenue-earnings-total-value">
                {currencySymbol} {total.toLocaleString()}
              </p>
            </div>
            <div className="text-center sm:text-right mt-2 sm:mt-0">
              <p className="text-sm font-medium text-gray-600" data-testid="revenue-earnings-average-label">
                {t('dashboard.average_monthly')}
              </p>
              <p className="text-lg font-semibold text-[#4ABDBD]" data-testid="revenue-earnings-average-value">
                {currencySymbol} {avg.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Selected-country monthly graph */}
          {total > 0 ? (
            <div className="h-80 w-full" data-testid="revenue-earnings-chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthly} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={formatYAxis} />
                  <Tooltip content={customTooltip} />
                  <Line type="monotone" dataKey="amount" stroke="#4ABDBD" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-80 flex flex-col items-center justify-center" data-testid="revenue-earnings-no-data">
              <span className="text-5xl text-gray-300 mb-2 font-bold" data-testid="revenue-earnings-no-data-symbol">{currencySymbol}</span>
              <p className="text-sm text-gray-600" data-testid="revenue-earnings-no-data-text">
                {t('dashboard.no_earnings_for_year', { year: currentYear })}
              </p>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default RevenueEarnings;
